import React, { useState, useContext } from 'react';
import { View, Text, Image, Pressable, StyleSheet, Modal } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import ImageListModal from './ImageListModal';
import { toast } from 'react-toastify';
import { GlobalStateContext } from '../../../../components/general/GlobalStateProvider';
import DataOps from '../../../../data/DataOps';
import { v4 as uuidv4 } from 'uuid';

const ImageGallery = ({ camp, isEditing = false, setCamp }) => {
    const { userCampId } = useContext(GlobalStateContext);
    
    // Check if current user is the host of this camp
    const isHost = userCampId === camp?.campId;
    const canEdit = isEditing;
    
    const campLogoUrl = String(camp?.campLogoUrl || '').trim();
    const originalImageUrls = Array.isArray(camp?.campImageUrls) ? camp.campImageUrls : [];
    const galleryEntries = originalImageUrls
        .map((uri, originalIndex) => ({ uri, originalIndex }))
        .filter((entry) => {
            const uri = String(entry.uri || '').trim();
            if (!uri) return false;
            if (campLogoUrl && uri === campLogoUrl) return false;
            return true;
        });
    const validImages = galleryEntries.map((entry) => ({ uri: entry.uri }));
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [visible, setVisible] = useState(false);
    const [showImageList, setShowImageList] = useState(false);

    const openGallery = (index) => {
        if (!validImages.length) return;
        setCurrentImageIndex(index);
        setVisible(true);
    };

    const nextImage = () => {
        if (!validImages.length) return;
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % validImages.length);
    };

    const prevImage = () => {
        if (!validImages.length) return;
        setCurrentImageIndex((prevIndex) => (prevIndex - 1 + validImages.length) % validImages.length);
    };

    const handleReorderImages = (fromIndex: number, toIndex: number) => {
        if (camp && camp.campImageUrls) {
            const newImageUrls = [...camp.campImageUrls];
            const newFileInfo = [...(camp.campFileInfo || [])];

            const fromOriginalIndex = galleryEntries[fromIndex]?.originalIndex;
            const toOriginalIndex = galleryEntries[toIndex]?.originalIndex;
            if (fromOriginalIndex === undefined || toOriginalIndex === undefined) return;
            
            // Move both the URL and the file info
            const [movedImage] = newImageUrls.splice(fromOriginalIndex, 1);
            const [movedFileInfo] = newFileInfo.splice(fromOriginalIndex, 1);
            
            newImageUrls.splice(toOriginalIndex, 0, movedImage);
            newFileInfo.splice(toOriginalIndex, 0, movedFileInfo);
            
            setCamp({ 
                ...camp, 
                campImageUrls: newImageUrls,
                campFileInfo: newFileInfo
            });
        }
    };

    const handleAddImage = async (files?: FileList) => {
        if (files) {
            const newFiles = Array.from(files);
            if (camp) {
                const currentUrls = camp.campImageUrls || [];
                const currentFileInfo = camp.campFileInfo || [];
                
                // Check for duplicates
                const duplicateFiles = newFiles.filter(newFile => 
                    currentFileInfo.some(existingFile => 
                        existingFile.name === newFile.name && 
                        existingFile.size === newFile.size
                    )
                );
                
                if (duplicateFiles.length > 0) {
                    const duplicateNames = duplicateFiles.map(f => f.name).join(', ');
                    toast.warning(`Duplicate images detected: ${duplicateNames}. These images have already been added and will be skipped.`);
                }
                
                const uniqueNewFiles = newFiles.filter(newFile => {
                    return !currentFileInfo.some(existingFile => 
                        existingFile.name === newFile.name && 
                        existingFile.size === newFile.size
                    );
                });
                
                if (uniqueNewFiles.length > 0) {
                    try {
                        toast.info('Uploading images...');
                        
                        // Upload each file to Firebase Storage
                        const newImageUrls = [];
                        const newFileInfo = [];
                        
                        for (const file of uniqueNewFiles) {
                            const imageId = uuidv4();
                            const blobUrl = URL.createObjectURL(file);
                            const permanentUrl = await DataOps.uploadImage(blobUrl, imageId);
                            
                            newImageUrls.push(permanentUrl);
                            newFileInfo.push({
                                name: file.name,
                                size: file.size,
                                type: file.type,
                                uploaded: true,
                                imageId: imageId
                            });
                        }
                        
                        // Update camp data
                        const updatedCamp = {
                            ...camp,
                            campImageUrls: [...currentUrls, ...newImageUrls],
                            campFileInfo: [...currentFileInfo, ...newFileInfo]
                        };
                        
                        // Save to Firebase
                        await DataOps.saveCustomDataToFirebase('camp', camp.campId, updatedCamp, true);
                        
                        // Update local state
                        setCamp(updatedCamp);
                        
                        if (uniqueNewFiles.length === 1) {
                            toast.success(`Successfully added: ${uniqueNewFiles[0].name}`);
                        } else {
                            toast.success(`Successfully added ${uniqueNewFiles.length} new images`);
                        }
                    } catch (error) {
                        console.error('Error uploading images:', error);
                        toast.error('Failed to upload images. Please try again.');
                    }
                }
            }
        } else {
            // Fallback: create file input (for backward compatibility)
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.multiple = true;
            
            input.onchange = (event) => {
                const selectedFiles = (event.target as HTMLInputElement).files;
                if (selectedFiles && selectedFiles.length > 0) {
                    handleAddImage(selectedFiles);
                }
            };
            
            input.click();
        }
    };

    const handleDeleteImage = async (index: number) => {
        if (camp && camp.campImageUrls) {
            try {
                const originalIndex = galleryEntries[index]?.originalIndex;
                if (originalIndex === undefined) return;
                const newImageUrls = [...camp.campImageUrls];
                const newFileInfo = [...(camp.campFileInfo || [])];
                
                // Remove the image and file info
                const deletedImageName = newFileInfo[originalIndex]?.name || 'image';
                newImageUrls.splice(originalIndex, 1);
                newFileInfo.splice(originalIndex, 1);
                
                // Update camp data
                const updatedCamp = {
                    ...camp,
                    campImageUrls: newImageUrls,
                    campFileInfo: newFileInfo
                };
                
                // Save to Firebase
                await DataOps.saveCustomDataToFirebase('camp', camp.campId, updatedCamp, true);
                
                // Update local state
                setCamp(updatedCamp);
                
                // Reset current image index if needed
                if (currentImageIndex >= newImageUrls.length) {
                    setCurrentImageIndex(Math.max(0, newImageUrls.length - 1));
                }
                
                toast.success(`Successfully deleted: ${deletedImageName}`);
            } catch (error) {
                console.error('Error deleting image:', error);
                toast.error('Failed to delete image. Please try again.');
            }
        }
    };

    const handleSaveChanges = async () => {
        toast.success('All changes are already saved!');
    };

    return (
        <View>
            <View style={styles.container}>
                {/* First column - 50% width, single large image */}
                <View style={styles.firstColumn}>
                    {validImages[0] ? (
                        <Pressable onPress={() => openGallery(0)} style={styles.mainImageWrapper}>
                            <Image source={validImages[0]} style={styles.mainImage} />
                        </Pressable>
                    ) : (
                        <View style={[styles.mainImageWrapper, styles.emptyImageCard]}>
                            <MaterialCommunityIcons name="image-outline" size={42} color="#9CA3AF" />
                            <Text style={styles.emptyImageText}>No gallery photos yet</Text>
                        </View>
                    )}
                </View>

                {/* Second column - 25% width, 2 rows */}
                <View style={styles.secondColumn}>
                    {validImages[1] ? (
                        <Pressable onPress={() => openGallery(1)} style={styles.thumbnailWrapper}>
                            <Image source={validImages[1]} style={styles.thumbnail} />
                        </Pressable>
                    ) : <View style={[styles.thumbnailWrapper, styles.emptyTile]} />}
                    {validImages[2] ? (
                        <Pressable onPress={() => openGallery(2)} style={styles.thumbnailWrapper}>
                            <Image source={validImages[2]} style={styles.thumbnail} />
                        </Pressable>
                    ) : <View style={[styles.thumbnailWrapper, styles.emptyTile]} />}
                </View>

                {/* Third column - 25% width, 2 rows */}
                <View style={styles.thirdColumn}>
                    {validImages[3] ? (
                        <Pressable onPress={() => openGallery(3)} style={styles.thumbnailWrapper}>
                            <Image source={validImages[3]} style={styles.thumbnail} />
                        </Pressable>
                    ) : <View style={[styles.thumbnailWrapper, styles.emptyTile]} />}
                    {validImages[4] ? (
                        <Pressable onPress={() => openGallery(4)} style={styles.thumbnailWrapper}>
                            <Image source={validImages[4]} style={styles.thumbnail} />
                        </Pressable>
                    ) : <View style={[styles.thumbnailWrapper, styles.emptyTile]} />}
                </View>
            </View>

            {/* Full screen modal */}
            {visible && (
                <Modal visible={visible} transparent={true} onRequestClose={() => setVisible(false)}>
                    <View style={styles.modalContainer}>
                        <Pressable onPress={() => setVisible(false)} style={styles.modalOverlay}>
                            <View style={styles.modalContent}>
                                <Pressable onPress={() => setVisible(false)} style={styles.closeButton}>
                                    <Text style={styles.closeButtonText}>✕</Text>
                                </Pressable>
                                
                                {validImages[currentImageIndex] ? (
                                    <Image source={validImages[currentImageIndex]} style={styles.fullImage} />
                                ) : null}
                                
                                {/* Navigation arrows */}
                                <Pressable style={styles.navButton} onPress={prevImage}>
                                    <Text style={styles.navButtonText}>‹</Text>
                                </Pressable>
                                
                                <Pressable style={[styles.navButton, styles.navButtonRight]} onPress={nextImage}>
                                    <Text style={styles.navButtonText}>›</Text>
                                </Pressable>
                                
                                {/* Image counter */}
                                <View style={styles.imageCounter}>
                                    <Text style={styles.counterText}>
                                        {Math.min(currentImageIndex + 1, validImages.length)} / {validImages.length}
                                    </Text>
                                </View>
                            </View>
                        </Pressable>
                    </View>
                </Modal>
            )}
            
            {/* Image List Modal */}
            <ImageListModal
                visible={showImageList}
                onClose={() => setShowImageList(false)}
                images={validImages}
                isEditing={canEdit}
                onDeleteImage={handleDeleteImage}
                onAddImage={handleAddImage}
                onReorderImages={handleReorderImages}
            />
            
            {/* Action Buttons - Bottom Right Corner */}
            <View style={styles.buttonContainer}>
                <Pressable 
                    style={[styles.actionButton, !canEdit && styles.viewAllPhotosButton]}
                    onPress={() => setShowImageList(true)}
                >
                    <MaterialCommunityIcons 
                        name={canEdit ? "camera-plus" : "image-multiple"} 
                        size={20} 
                        color="white" 
                    />
                    <Text style={styles.buttonText}>
                        {canEdit ? 'Update Photos' : 'View All Photos'}
                    </Text>
                </Pressable>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        height: 400,
        marginBottom: 20,
        gap: 4,
    },
    firstColumn: {
        flex: 2,
    },
    secondColumn: {
        flex: 1,
        flexDirection: 'column',
        gap: 4,
    },
    thirdColumn: {
        flex: 1,
        flexDirection: 'column',
        gap: 4,
    },
    mainImageWrapper: {
        width: '100%',
        height: '100%',
        borderRadius: 12,
        overflow: 'hidden',
    },
    mainImage: {
        width: '100%',
        height: '100%',
        resizeMode: 'contain',
    },
    emptyImageCard: {
        backgroundColor: '#F8FAFC',
        justifyContent: 'center',
        alignItems: 'center',
    },
    emptyImageText: {
        marginTop: 10,
        color: '#6B7280',
        fontSize: 14,
        fontWeight: '500',
    },
    thumbnailWrapper: {
        flex: 1,
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: '#f0f0f0',
    },
    emptyTile: {
        backgroundColor: '#F1F5F9',
    },
    thumbnail: {
        width: '100%',
        height: '100%',
        resizeMode: 'contain',
    },
    showAllButton: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: 12,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 8,
    },
    showAllContent: {
        alignItems: 'center',
    },
    showAllText: {
        color: 'white',
        fontSize: 16,
        fontWeight: '600',
    },
    modalContainer: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
    },
    modalOverlay: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContent: {
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
    },
    closeButton: {
        position: 'absolute',
        top: 50,
        right: 20,
        zIndex: 10,
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    closeButtonText: {
        color: 'white',
        fontSize: 20,
        fontWeight: 'bold',
    },
    fullImage: {
        width: '90%',
        height: '80%',
        resizeMode: 'contain',
    },
    navButton: {
        position: 'absolute',
        left: 20,
        top: '50%',
        transform: [{ translateY: -20 }],
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    navButtonRight: {
        left: 'auto',
        right: 20,
    },
    navButtonText: {
        color: 'white',
        fontSize: 24,
        fontWeight: 'bold',
    },
    imageCounter: {
        position: 'absolute',
        bottom: 50,
        left: 0,
        right: 0,
        alignItems: 'center',
    },
    counterText: {
        color: 'white',
        fontSize: 16,
        fontWeight: '600',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 15,
    },
    buttonContainer: {
        position: 'absolute',
        bottom: 40,
        right: 20,
        flexDirection: 'row',
        gap: 12,
        zIndex: 10,
    },
    actionButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#000000',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderRadius: 8,
        gap: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
    },
    editButton: {
        backgroundColor: '#34C759',
    },
    viewAllPhotosButton: {
        backgroundColor: '#000000',
    },
    buttonText: {
        color: 'white',
        fontSize: 14,
        fontWeight: '600',
    },
});

export default ImageGallery;
